import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import axios from "axios";
import config from "react-global-configuration";
import { BeatLoader } from "react-spinners";
import { css } from "@emotion/core";
import "../../assets/css/datatables/datatables.min.css";
import "./MondayOKRDrilldown.css";

const spinnerOverride = css`
    display: block;
    margin: 40px auto;
`;

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100, 500];

// Identity (non-metric) columns returned by the drilldown endpoints. Used to
// pick a sensible default sort column when the requested one isn't present.
const IDENTITY_COLUMNS = new Set(["Merchant Name", "Mobile", "Merchant Mobile"]);

// Columns the backend cannot sort by, so we don't make them clickable.
// (Merchant Name IS backend-sortable; the mobile columns are not.)
const NON_SORTABLE_COLUMNS = new Set(["Mobile", "Merchant Mobile"]);

const ONE_TIME_LINK_PRODUCTS = new Set([
    "Innoviti Link",
    "Innoviti Link - Healthcare",
]);

const getDefaultSortColumn = (metric, product) => {
    if (metric === "One-time Revenue" && ONE_TIME_LINK_PRODUCTS.has(product)) {
        return "One-time Revenue";
    }
    switch (metric) {
        case "Transactable Stores":
        case "Transacting Stores":
            return "Store Count";
        case "Transactable UTIDs":
        case "Transacting UTIDs":
        case "Newly Acquired UTIDs":
        case "Churned UTIDs":
            return "UTID Count";
        case "Tickets Open Outside TAT":
            return "Ticket Count";
        case "Newly Acquired Merchants":
        case "Transactable Merchants":
        case "Transacting Merchants":
            return "Merchant Name";
        case "Blocks (net of Unblocks)":
            return "Status";
        default:
            return "GTV (Rs.)";
    }
};

const buildPageList = (current, total) => {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = new Set([1, total, current, current - 1, current + 1]);
    if (current <= 4) {
        pages.add(2);
        pages.add(3);
        pages.add(4);
        pages.add(5);
    }
    if (current >= total - 3) {
        pages.add(total - 1);
        pages.add(total - 2);
        pages.add(total - 3);
        pages.add(total - 4);
    }
    const sorted = Array.from(pages)
        .filter((p) => p >= 1 && p <= total)
        .sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
            result.push("...");
        }
        result.push(sorted[i]);
    }
    return result;
};

class MondayOKRDrilldown extends Component {
    constructor(props) {
        super(props);
        const params = new URLSearchParams(props.location.search);
        const product = params.get("product") || "";
        const metric = params.get("metric") || "";
        this.state = {
            product,
            metric,
            data: [],
            columns: [],
            recordsTotal: 0,
            pageNumber: 1,
            rowsPerPage: 20,
            searchQuery: "",
            searchInput: "",
            sortColumn: getDefaultSortColumn(metric, product),
            sortOrder: metric === "Newly Acquired Merchants" ||
                metric === "Transactable Merchants" ||
                metric === "Transacting Merchants" ||
                metric === "Blocks (net of Unblocks)"
                ? "ASC"
                : "DESC",
            loading: false,
            error: "",
            downloading: false,
        };
        this.searchTimer = null;
    }

    componentDidMount() {
        this.props.currentMenu && this.props.currentMenu("unipaynext");
        this.fetchData();
    }

    componentWillUnmount() {
        if (this.searchTimer) clearTimeout(this.searchTimer);
    }

    fetchData = () => {
        const { product, metric, pageNumber, rowsPerPage, searchQuery, sortColumn, sortOrder } = this.state;
        if (!product || !metric) {
            this.setState({ error: "Missing product or metric" });
            return;
        }
        this.setState({ loading: true, error: "" });
        axios
            .post(
                config.get("API_URL") + "/reportdashboard/get-monday-okr-drilldown",
                {
                    product,
                    metric,
                    start: (pageNumber - 1) * rowsPerPage,
                    length: rowsPerPage,
                    search: { value: searchQuery },
                    download: 0,
                    sort_column: sortColumn,
                    sort_order: sortOrder,
                },
                { headers: { Authorization: ` ${localStorage.usertoken}` } }
            )
            .then((response) => {
                const payload = response.data || {};
                if (payload.error) {
                    this.setState({
                        loading: false,
                        data: [],
                        columns: [],
                        recordsTotal: 0,
                        error: payload.error,
                    });
                    return;
                }
                const rows = payload.data || [];
                const columns = rows.length > 0 ? Object.keys(rows[0]) : [];
                // The backend falls back to the first metric (period) column when the
                // requested sort column isn't one of the returned columns. Mirror that
                // here so the active-sort indicator lines up with the actual ordering.
                let sortColumn = this.state.sortColumn;
                if (columns.length > 0 && !columns.includes(sortColumn)) {
                    sortColumn =
                        columns.find((c) => !IDENTITY_COLUMNS.has(c)) || columns[0];
                }
                this.setState({
                    loading: false,
                    data: rows,
                    columns,
                    sortColumn,
                    recordsTotal: payload.recordsTotal || 0,
                });
            })
            .catch((err) => {
                console.error("get-monday-okr-metric-drilldown error", err);
                this.setState({
                    loading: false,
                    data: [],
                    columns: [],
                    recordsTotal: 0,
                    error: "Failed to load drilldown",
                });
            });
    };

    handleDownload = () => {
        const { product, metric, searchQuery, downloading, sortColumn, sortOrder } = this.state;
        if (downloading) return;
        if (!product || !metric) return;

        this.setState({ downloading: true });
        axios
            .post(
                config.get("API_URL") + "/reportdashboard/get-monday-okr-drilldown",
                {
                    product,
                    metric,
                    start: 0,
                    length: 0,
                    search: { value: searchQuery },
                    download: 1,
                    sort_column: sortColumn,
                    sort_order: sortOrder,
                },
                { headers: { Authorization: ` ${localStorage.usertoken}` } }
            )
            .then((response) => {
                const payload = response.data || {};
                if (payload.error) {
                    alert(`Download failed: ${payload.error}`);
                    this.setState({ downloading: false });
                    return;
                }
                const paths = Array.isArray(payload.data) ? payload.data : [payload.data];
                const validPaths = paths.filter((p) => p);
                if (validPaths.length === 0) {
                    alert("No file to download");
                    this.setState({ downloading: false });
                    return;
                }
                Promise.all(
                    validPaths.map((path, idx) =>
                        this.copyRemoteFile(path, idx, validPaths.length)
                    )
                )
                    .then(() => this.setState({ downloading: false }))
                    .catch(() => this.setState({ downloading: false }));
            })
            .catch((err) => {
                console.error("download error", err);
                alert("Failed to download. Please try again.");
                this.setState({ downloading: false });
            });
    };

    copyRemoteFile = (remotePath, index, totalFiles) => {
        const { product, metric } = this.state;
        return axios
            .post(
                config.get("API_URL") + "/reports/copy-file",
                { remotePath },
                {
                    headers: { Authorization: ` ${localStorage.usertoken}` },
                    responseType: "blob",
                }
            )
            .then((response) => {
                const blob = new Blob([response.data], { type: "octet/stream" });
                const sanitize = (s) => (s || "").replace(/[\\/:*?"<>|]+/g, "_").trim();
                const baseName = `${sanitize(product)}_${sanitize(metric)}`;
                const fileName =
                    totalFiles > 1
                        ? `${baseName}_${index + 1}.csv.gz`
                        : `${baseName}.csv.gz`;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            });
    };

    goToPage = (page) => {
        const totalPages = this.totalPages();
        const clamped = Math.max(1, Math.min(page, totalPages || 1));
        if (clamped === this.state.pageNumber) return;
        this.setState({ pageNumber: clamped }, this.fetchData);
    };

    handleRowsPerPageChange = (e) => {
        const rowsPerPage = parseInt(e.target.value, 10) || 20;
        this.setState({ rowsPerPage, pageNumber: 1 }, this.fetchData);
    };

    handleSort = (column) => {
        if (NON_SORTABLE_COLUMNS.has(column)) return;
        const { sortColumn, sortOrder, loading } = this.state;
        if (loading) return;
        const nextOrder =
            sortColumn === column ? (sortOrder === "DESC" ? "ASC" : "DESC") : "DESC";
        this.setState(
            { sortColumn: column, sortOrder: nextOrder, pageNumber: 1 },
            this.fetchData
        );
    };

    handleSearchChange = (e) => {
        const value = e.target.value;
        this.setState({ searchInput: value });
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
            this.setState(
                { searchQuery: value.trim(), pageNumber: 1 },
                this.fetchData
            );
        }, 400);
    };

    handleBack = () => {
        this.props.history.push("/admin/monday-okr-report-v4");
    };

    totalPages = () => {
        const { recordsTotal, rowsPerPage } = this.state;
        return Math.max(1, Math.ceil((recordsTotal || 0) / rowsPerPage));
    };

    renderPagination = () => {
        const { pageNumber, loading } = this.state;
        const totalPages = this.totalPages();
        const pages = buildPageList(pageNumber, totalPages);
        const prevDisabled = loading || pageNumber <= 1;
        const nextDisabled = loading || pageNumber >= totalPages;

        return (
            <div className="dataTables_wrapper okr-dd-dt-wrapper">
                <div className="dataTables_paginate paging_simple_numbers">
                    <a
                        className={`paginate_button previous ${prevDisabled ? "disabled" : ""}`}
                        onClick={prevDisabled ? undefined : () => this.goToPage(pageNumber - 1)}
                    >
                        Previous
                    </a>
                    <span>
                        {pages.map((p, idx) =>
                            p === "..." ? (
                                <span key={`ellipsis-${idx}`} className="ellipsis">
                                    …
                                </span>
                            ) : (
                                <a
                                    key={p}
                                    className={`paginate_button ${p === pageNumber ? "current" : ""}`}
                                    onClick={
                                        loading || p === pageNumber
                                            ? undefined
                                            : () => this.goToPage(p)
                                    }
                                >
                                    {p}
                                </a>
                            )
                        )}
                    </span>
                    <a
                        className={`paginate_button next ${nextDisabled ? "disabled" : ""}`}
                        onClick={nextDisabled ? undefined : () => this.goToPage(pageNumber + 1)}
                    >
                        Next
                    </a>
                </div>
            </div>
        );
    };

    isNumericValue = (val) => {
        if (val === null || val === undefined || val === "") return false;
        if (typeof val === "number") return true;
        return !isNaN(Number(val));
    };

    formatCell = (val, columnName) => {
        if (val === null || val === undefined || val === "") return "-";
        if (!this.isNumericValue(val)) return val;
        if (columnName === "Mobile" || columnName === "Merchant Mobile") return val;
        const str = String(val);
        const dotIdx = str.indexOf(".");
        const fractionDigits = dotIdx === -1 ? 0 : str.length - dotIdx - 1;
        return Number(val).toLocaleString("en-IN", {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        });
    };

    render() {
        const {
            product,
            metric,
            data,
            columns,
            loading,
            error,
            rowsPerPage,
            searchInput,
            downloading,
            sortColumn,
            sortOrder,
        } = this.state;

        return (
            <div className="content-invoice monday-okr-drilldown">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-12 pt-5">
                            <div className="card okr-dd-card">
                                <div className="okr-dd-header-row">
                                    <div className="okr-dd-title-block">
                                        <button
                                            className="okr-dd-back-btn"
                                            onClick={this.handleBack}
                                            type="button"
                                        >
                                            &larr; Back
                                        </button>
                                        <div className="okr-dd-title-text">
                                            <h3 className="okr-dd-title">{metric || "Drilldown"}</h3>
                                            <span className="okr-dd-subtitle">{product}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="okr-dd-toolbar">
                                    <div className="okr-dd-toolbar-left">
                                        <label className="okr-dd-toolbar-label">Search</label>
                                        <input
                                            type="text"
                                            className="okr-dd-search-input"
                                            placeholder="Filter by merchant name..."
                                            value={searchInput}
                                            onChange={this.handleSearchChange}
                                        />
                                    </div>
                                    <div className="okr-dd-toolbar-right">
                                        <label className="okr-dd-toolbar-label">Rows</label>
                                        <select
                                            className="okr-dd-rows-select"
                                            value={rowsPerPage}
                                            onChange={this.handleRowsPerPageChange}
                                        >
                                            {ROWS_PER_PAGE_OPTIONS.map((opt) => (
                                                <option key={opt} value={opt}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className="okr-dd-download-btn"
                                            onClick={this.handleDownload}
                                            disabled={downloading || loading}
                                        >
                                            {downloading ? (
                                                "Downloading..."
                                            ) : (
                                                <>
                                                    <i className="fa fa-download" aria-hidden="true" />
                                                    &nbsp;Download CSV
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {error && (
                                    <div className="okr-dd-error">{error}</div>
                                )}

                                <div className="okr-dd-table-wrapper table-responsive">
                                    <table className="table okr-dd-table">
                                        <thead>
                                            <tr>
                                                {columns.length === 0 ? (
                                                    <th>&nbsp;</th>
                                                ) : (
                                                    columns.map((c) => {
                                                        const sortable = !NON_SORTABLE_COLUMNS.has(c);
                                                        const isActive = sortable && sortColumn === c;
                                                        const indicator = isActive
                                                            ? sortOrder === "ASC"
                                                                ? " ▲"
                                                                : " ▼"
                                                            : sortable
                                                                ? " ⇅"
                                                                : "";
                                                        return (
                                                            <th
                                                                key={c}
                                                                className={
                                                                    sortable
                                                                        ? `okr-dd-th-sortable${isActive ? " okr-dd-th-active" : ""}`
                                                                        : ""
                                                                }
                                                                onClick={
                                                                    sortable
                                                                        ? () => this.handleSort(c)
                                                                        : undefined
                                                                }
                                                            >
                                                                {c}
                                                                <span className="okr-dd-sort-indicator">
                                                                    {indicator}
                                                                </span>
                                                            </th>
                                                        );
                                                    })
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td
                                                        colSpan={Math.max(columns.length, 1)}
                                                        className="okr-dd-loading-cell"
                                                    >
                                                        <BeatLoader
                                                            css={spinnerOverride}
                                                            color={"#3B82F6"}
                                                            loading={true}
                                                        />
                                                    </td>
                                                </tr>
                                            ) : data.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={Math.max(columns.length, 1)}
                                                        className="okr-dd-empty-cell"
                                                    >
                                                        No data available
                                                    </td>
                                                </tr>
                                            ) : (
                                                data.map((row, idx) => (
                                                    <tr key={`drow-${idx}`}>
                                                        {columns.map((c) => (
                                                            <td
                                                                key={c}
                                                                className={
                                                                    this.isNumericValue(row[c])
                                                                        ? "okr-dd-num"
                                                                        : ""
                                                                }
                                                            >
                                                                {this.formatCell(row[c], c)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {this.renderPagination()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    currentMenu: (name) => {
        dispatch({ type: "change_menu", payload: name });
    },
});

export default withRouter(connect(null, mapDispatchToProps)(MondayOKRDrilldown));
